import React, { useMemo, useState } from "react";
import { Accordion, Alert, Badge, Button, Card, Dropdown, Form, Spinner, Table } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router-dom";

import { useAppApi } from "@/App";
import { moderationState } from "@/const";
import { useRequest } from "@components/AppHook";
import { ExportButton } from "@components/Common/Button";
import { DevForm, FormFieldGroup } from "@components/Forms/Fields";
import { useSubmit } from "@components/Forms/Handlers";
import { RenderForm } from "@components/Forms/Render";
import { Awesome, DevModal } from "@components/Helpers";
import { DefaultLayout } from "@components/Layouts";
import { arrayMove, buildGetParamsFromApiData, buildQS, url_reverse } from "@components/Utils";
import { DndContext, PointerSensor, closestCenter, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const publicationState = {
    configuration: "Конфигурация проекта",
    configuration_marketing: "Конфигурация проекта (маркетинг)",
    testing: "Техническая модерация проекта",
    testing_marketing: "Маркетинговая модерация проекта",
    done: "Модерация проекта пройдена",
    unpublish_request: "Запрос на снятие с публикации",
    unpublished: "Снято с публикации",
};
export default function EventSelectionItemListScreen() {
    const { t } = useTranslation();
    const { slug } = useParams();
    const apiUrl = url_reverse("api:event_items:list", { event_slug: slug });
    const { apiData, setApiData } = useAppApi(apiUrl);
    const navigate = useNavigate();
    const [showFilterModel, setShowFilterModal] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [errors, setErrors] = useState({});
    const [apiSelectionGameData, setApiSelectionGameData] = useState({});
    const [selectionEditObject, setSelectionEditObject] = useState({});
    const [showEditSelectionModal, setShowEditSelectionModal] = useState(false);
    const [activeSelectionId, setActiveSelectionId] = useState(localStorage.getItem("activeSelectionId"));
    const [selectedGame, setSelectedGame] = useState([]);
    const [isOpen, setIsOpen] = useState(false);
    const [isLoaded, setIsLoaded] = useState(false);
    const [deleteNonParticipant, setDeleteNonParticipant] = useState(0);
    const [showEventSetting, setShowEventSetting] = useState(false);
    const onFormSubmit = useSubmit();
    const { api_get, api_post } = useRequest();
    const [trackerLoading, setTrackerLoading] = useState("");

    if (!apiData) return;

    const handleGameUpload = (selection_id) => {
        setIsLoaded(true);
        if (selection_id) {
            api_get(null, url_reverse("api:event:selection:item:list", { event_slug: slug, selection_id }), {
                then: (resp) => {
                    if (resp.status === "ok") {
                        setApiSelectionGameData(resp);
                        localStorage.setItem("activeSelectionId", selection_id);
                        setErrors({});
                    } else if (resp.status === "fail") {
                        setErrors(resp.errors);
                    }
                },
            });
        }
    };

    const isActiveExists = apiData?.selections.some((obj) => obj.id === parseInt(activeSelectionId));
    if (isActiveExists && !isLoaded) {
        handleGameUpload(activeSelectionId);
    } else if (!isLoaded) {
        handleGameUpload(apiData?.initial_selection_id?.toString());
        setActiveSelectionId(apiData?.initial_selection_id?.toString());
    }

    const handleModalClose = () => {
        const newApiData = { ...apiData };
        newApiData.forms_data.create_selection.data = {};
        setApiData(newApiData);
        setShowModal(false);
    };

    const handleEventSettingClose = () => {
        const newApiData = { ...apiData };
        setApiData(newApiData);
        setShowEventSetting(false);
    };

    const header = (
        <>
            <Link to={url_reverse("admin:event:list")}>{t("Ивенты")}</Link>
            <span> – </span>
            {t("Управление ивентом")}{" "}
            <a onClick={() => setShowEventSetting(true)} data-toggle="tooltip" data-placement="top">
                <Awesome type="pencil" color="dodgerblue" />
            </a>{" "}
        </>
    );
    const headerTools = (
        <>
            <Button
                className="mr-1"
                size="sm"
                variant="dark"
                as={Link}
                to={url_reverse("admin:event:generate_serials", { slug })}
            >
                <Awesome type="key" tooltip={t("Генерация ключей")} />
            </Button>
            <Button
                className="mr-1"
                size="sm"
                variant="dark"
                as={Link}
                to={url_reverse("admin:event:send_serials", { slug })}
            >
                <Awesome type="envelope" tooltip={t("Рассылка ключей")} />
            </Button>
            <ExportButton
                className="btn btn-sm btn-success"
                apiUrl={apiUrl}
                prms={{
                    report_type: "create_report",
                }}
            >
                <Awesome type="cloud-download" tooltip={t("Отчет по играм, готовым к размещению")} />
            </ExportButton>{" "}
            <Button size="sm" variant="dark" as={Link} to={url_reverse("admin:event:settings", { slug })}>
                {t("Настройки кабинета")}
            </Button>{" "}
            <Button size="sm" variant="dark" onClick={() => setShowModal(true)}>
                <Awesome type="plus-circle" /> {t("Создать подборку")}
            </Button>{" "}
        </>
    );

    const eventSettingModal = () => {
        return (
            <DevModal
                size="lg"
                show={showEventSetting}
                onHide={handleEventSettingClose}
                header={t("Настройки ивента")}
                body={
                    <>
                        <DevForm.Field {...{ apiData, setApiData }} prefix="event_setting" name="name" />
                        <DevForm.Field {...{ apiData, setApiData }} prefix="event_setting" name="scheme" />
                        <DevForm.Field {...{ apiData, setApiData }} prefix="event_setting" name="event_type" />
                    </>
                }
                footer={
                    <Button variant="primary" name="action" value="event_setting" type="submit">
                        {t("Сохранить")}
                    </Button>
                }
                renderWrapper={(children) => (
                    <Form
                        onSubmit={(ev) =>
                            onFormSubmit(ev, {
                                submitPrefixes: ["event_setting"],
                                onOK: (resp) => {
                                    const new_slug = resp.event.slug;
                                    navigate(url_reverse("admin:event:items", { slug: new_slug }));
                                    setShowEventSetting(false);
                                },
                            })
                        }
                    >
                        {children}
                    </Form>
                )}
            />
        );
    };

    const addSelectionModal = () => {
        return (
            <DevModal
                size="lg"
                show={showModal}
                onHide={handleModalClose}
                header={t("Создание выборки")}
                body={<DevForm.Field {...{ apiData, setApiData }} prefix="create_selection" name="name" />}
                footer={
                    <Button variant="primary" name="action" value="create_selection" type="submit">
                        {t("Создать")}
                    </Button>
                }
                renderWrapper={(children) => (
                    <Form
                        onSubmit={(ev) =>
                            onFormSubmit(ev, {
                                submitPrefixes: ["create_selection"],
                                onOK: () => {
                                    location.replace(location.href);
                                },
                            })
                        }
                    >
                        {children}
                    </Form>
                )}
            />
        );
    };

    const delete_selection = (selection) => {
        api_post(null, apiUrl, {
            params: { action: "delete_selection", selection_id: selection.id },
            then: (resp) => {
                if (resp.status === "ok") {
                    setApiData(resp);
                    setErrors({});
                } else if (resp.status === "fail") {
                    setErrors(resp.errors);
                }
            },
        });
    };

    const handleSelectionNameChange = (ev) => {
        const { value } = ev.target;
        setSelectionEditObject((prevState) => {
            return {
                ...prevState,
                name: value,
            };
        });
    };
    const submitSelectionNameChange = (ev) => {
        api_post(ev, apiUrl, {
            params: { action: "edit_selection", selection_id: selectionEditObject.id, name: selectionEditObject.name },
            then: (resp) => {
                if (resp.status === "ok") {
                    setApiData(resp);
                    setErrors({});
                    setShowEditSelectionModal(false);
                    setSelectionEditObject({});
                } else if (resp.status === "fail") {
                    setErrors(resp.errors);
                }
            },
        });
    };
    const EditSelectionModal = () => {
        return (
            <DevModal
                size="lg"
                show={showEditSelectionModal}
                onHide={() => {
                    setErrors({});
                    setSelectionEditObject({});
                    setShowEditSelectionModal(false);
                }}
                header={t("Редактировать выборку")}
                body={
                    <FormFieldGroup label={t("Имя:")}>
                        <DevForm.TextInput value={selectionEditObject.name} onChange={handleSelectionNameChange} />
                        {errors?.name && (
                            <div className="error" style={{ color: "red", marginLeft: "10px", marginTop: "5px" }}>
                                {errors.name}
                            </div>
                        )}
                    </FormFieldGroup>
                }
                footer={
                    <DevForm.Button variant="primary" name="save" onClick={submitSelectionNameChange}>
                        {t("Сохранить")}
                    </DevForm.Button>
                }
            />
        );
    };

    const handleGamesAction = (action) => {
        api_post(
            null,
            url_reverse("api:event:selection:item:list", {
                event_slug: slug,
                selection_id: activeSelectionId,
            }),
            {
                params: { game_ids: selectedGame, action },
                then: (resp) => {
                    if (resp.status === "ok") {
                        setApiSelectionGameData(resp);
                        api_get(null, apiUrl, {
                            then: (resp2) => setApiData(resp2),
                        });
                        setSelectedGame([]);
                    } else {
                        setErrors(resp.errors);
                    }
                },
            }
        );
    };

    const handleClose = () => setShowFilterModal(false);
    const filterAndClose = (ev) => {
        ev.preventDefault();

        const getParams = buildGetParamsFromApiData({
            apiData: apiSelectionGameData,
            prefix: "filter",
            filterEmptyStrings: true,
        });
        const url = url_reverse("api:event:selection:item:list", {
            event_slug: slug,
            selection_id: activeSelectionId,
        });
        api_get(ev, url + "?" + buildQS(getParams), {
            then: (resp) => {
                if (resp.status === "ok") {
                    setApiSelectionGameData(resp);
                }
                handleClose();
            },
        });
    };
    const handleReset = (ev) => {
        const resetFilterData = { ...apiSelectionGameData };
        resetFilterData.forms_data.filter.data = {};
        setApiSelectionGameData(resetFilterData);
        filterAndClose(ev);
    };

    const createLanding = (ev) => {
        const selection_id = ev.target.value;
        api_post(ev, url_reverse("api:landing:block_list", { selection_id }), {
            params: { selection_id, action: "create_landing" },
            then: (resp) => {
                if (resp.status === "ok") {
                    navigate(url_reverse("admin:event:landing", { slug, selection_id }));
                }
            },
        });
    };

    const filterModel = () => {
        return (
            <DevModal
                size="lg"
                show={showFilterModel}
                onHide={handleClose}
                header={t("Фильтр")}
                body={
                    <RenderForm
                        prefix={"filter"}
                        apiData={apiSelectionGameData}
                        setApiData={setApiSelectionGameData}
                        bareForm={true}
                    />
                }
                footer={
                    <>
                        <DevForm.Button variant="light" name="reset" onClick={handleReset}>
                            {t("Сброс")}
                        </DevForm.Button>
                        <DevForm.Button variant="primary" type="submit" name="search">
                            {t("Найти")}
                        </DevForm.Button>
                    </>
                }
                renderWrapper={(children) => <Form onSubmit={filterAndClose}>{children}</Form>}
            />
        );
    };
    const handleDeleteNonParticipant = (ev) => {
        const selection_id = ev.target.value;
        api_post(ev, url_reverse("api:event:selection:item:list", { event_slug: slug, selection_id }), {
            params: { action: "delete_not_participant" },
            then: (resp) => {
                if (resp.status === "ok") {
                    location.reload();
                }
            },
        });
    };
    const nonParticipantModal = () => {
        return (
            <DevModal
                show={deleteNonParticipant}
                onHide={() => setDeleteNonParticipant(0)}
                body={t("Подтвердите свое действие")}
                footer={
                    <DevForm.Button
                        variant="primary"
                        name="del_np"
                        value={deleteNonParticipant}
                        onClick={handleDeleteNonParticipant}
                    >
                        {t("Подтверждаю")}
                    </DevForm.Button>
                }
            />
        );
    };

    const redirectToMyTrackerStatistics = (ev) => {
        const selection_id = ev.target.value;
        // Индикатор загрузки обрабатываем вручную, потому что глобальный
        // почему-то заканчивается раньше, чем отработал запрос
        setTrackerLoading(`mytracker-stats-button-${selection_id}`);
        api_post(ev, url_reverse("api:event:statistics", { event_slug: slug, selection_id }), {
            then: (resp) => {
                if (resp.status === "ok") {
                    window
                        .open(`https://app.tracker.my.com/report/constructor?idReport=${resp.report_id}`, "_blank")
                        .focus();
                }
                setTrackerLoading("");
            },
        });
    };

    return (
        <DefaultLayout>
            <Card>
                {errors?.selection_not_empty && <Alert variant="danger">{errors.selection_not_empty}</Alert>}
                {addSelectionModal()}
                {filterModel()}
                {nonParticipantModal()}
                {eventSettingModal()}
                <Card.Header>
                    {header}
                    <div className="dmg-card-header-tools">{headerTools}</div>
                </Card.Header>
                <Card.Body>
                    <div style={{ marginBottom: "20px" }}>
                        <Link
                            to={url_reverse("admin:event:send_newsletter", {
                                event_slug: slug,
                            })}
                        >
                            {t("Рассылка")}
                        </Link>
                    </div>
                    <Accordion>
                        {apiData?.selections?.map((selection) => {
                            return (
                                <React.Fragment key={selection.id}>
                                    <Card style={{ cursor: "pointer", overflowY: "initial" }}>
                                        <Accordion.Toggle
                                            as={Card.Header}
                                            eventKey={selection.id}
                                            onClick={() => {
                                                if (selection.id !== activeSelectionId) {
                                                    handleGameUpload(selection.id);
                                                    setActiveSelectionId(selection.id);
                                                }
                                            }}
                                        >
                                            <div
                                                className="dmg-table-row-tools"
                                                style={{ display: "flex", justifyContent: "space-between" }}
                                            >
                                                <div className="left-tools d-flex align-items-center">
                                                    <b className="mr-2">{selection.name}</b>
                                                    <Button
                                                        size="sm"
                                                        variant="outline-primary"
                                                        className="mr-1"
                                                        onClick={() => {
                                                            setSelectionEditObject(selection);
                                                            setShowEditSelectionModal(true);
                                                        }}
                                                    >
                                                        <Awesome type="pencil" />
                                                    </Button>
                                                    <EditSelectionModal />
                                                    <Button
                                                        size="sm"
                                                        variant="outline-danger"
                                                        onClick={() => {
                                                            delete_selection(selection);
                                                        }}
                                                    >
                                                        <Awesome type="trash-o" />
                                                    </Button>
                                                </div>
                                                <div className="right-tools d-flex align-items-center">
                                                    <Badge variant="info">
                                                        {t("Кол-во игр")}: {selection.count}
                                                    </Badge>
                                                    <Badge variant="info">
                                                        {t("Готовы к размещению")}: {selection.__review_passed}
                                                    </Badge>
                                                    <Button
                                                        href="#"
                                                        variant="outline-danger"
                                                        size="sm"
                                                        onClick={() => setDeleteNonParticipant(selection.id)}
                                                    >
                                                        <Awesome
                                                            type="paint-brush"
                                                            tooltip={t(
                                                                "Убрать из списка все игры, которые не готовы к размещению или исключены"
                                                            )}
                                                        />
                                                    </Button>
                                                    &nbsp;
                                                    <Button
                                                        href="#"
                                                        variant="dark"
                                                        size="sm"
                                                        onClick={() => setShowFilterModal(true)}
                                                    >
                                                        <Awesome type="filter" />
                                                    </Button>
                                                    &nbsp;
                                                    {selection?.id && (
                                                        <DevForm.Button
                                                            name="mytracker-stats-button"
                                                            size="sm"
                                                            value={selection.id}
                                                            isLoading={trackerLoading}
                                                            variant="dark"
                                                            onClick={redirectToMyTrackerStatistics}
                                                        >
                                                            {t("Статистика")}
                                                        </DevForm.Button>
                                                    )}
                                                    &nbsp;
                                                    {selection.landing_exists && (
                                                        <Button
                                                            size="sm"
                                                            variant="dark"
                                                            as={Link}
                                                            to={url_reverse("admin:event:landing", {
                                                                slug,
                                                                selection_id: selection.id,
                                                            })}
                                                        >
                                                            {t("Лендинг")}
                                                        </Button>
                                                    )}
                                                    {!selection.landing_exists && (
                                                        <DevForm.Button
                                                            size="sm"
                                                            variant="dark"
                                                            name="create_landing"
                                                            value={selection.id}
                                                            onClick={createLanding}
                                                        >
                                                            {t("Создать лендинг")}
                                                        </DevForm.Button>
                                                    )}
                                                    &nbsp;
                                                    <Dropdown show={isOpen} onToggle={() => setIsOpen(!isOpen)}>
                                                        <Dropdown.Toggle variant={"dark"} size={"sm"}>
                                                            {t("С выбранными")}
                                                        </Dropdown.Toggle>
                                                        <Dropdown.Menu>
                                                            <Dropdown.Item
                                                                onClick={() => handleGamesAction("delete_games")}
                                                            >
                                                                {t("Удалить")}
                                                            </Dropdown.Item>
                                                            <Dropdown.Item
                                                                onClick={() => handleGamesAction("exclude_games")}
                                                            >
                                                                {t("Исключить")}
                                                            </Dropdown.Item>
                                                            <Dropdown.Item
                                                                onClick={() => handleGamesAction("restore_games")}
                                                            >
                                                                {t("Восстановить")}
                                                            </Dropdown.Item>
                                                        </Dropdown.Menu>
                                                    </Dropdown>
                                                    &nbsp;
                                                    <Button
                                                        size="sm"
                                                        variant="dark"
                                                        as={Link}
                                                        to={url_reverse("admin:event:selection:items:add", {
                                                            event_slug: slug,
                                                            selection_id: selection.id,
                                                        })}
                                                        onClick={() => {
                                                            if (selection.id !== activeSelectionId) {
                                                                localStorage.setItem("activeSelectionId", selection.id);
                                                            }
                                                        }}
                                                    >
                                                        <Awesome type="plus-circle" /> {t("Добавить Игры")}
                                                    </Button>
                                                </div>
                                            </div>
                                        </Accordion.Toggle>
                                        <Accordion.Collapse
                                            eventKey={selection.id}
                                            in={selection.id === parseInt(activeSelectionId)}
                                        >
                                            <Card.Body style={{ minHeight: "150px" }}>
                                                {apiSelectionGameData &&
                                                    apiSelectionGameData.selection?.id === parseInt(selection.id) && (
                                                        <GamesListSortable
                                                            apiData={apiSelectionGameData}
                                                            setApiData={setApiSelectionGameData}
                                                            selectionId={selection.id}
                                                            setErrors={setErrors}
                                                            selectedGame={selectedGame}
                                                            setSelectedGame={setSelectedGame}
                                                            apiSelectionGameData={apiSelectionGameData}
                                                        />
                                                    )}
                                            </Card.Body>
                                        </Accordion.Collapse>
                                    </Card>
                                </React.Fragment>
                            );
                        })}
                    </Accordion>
                </Card.Body>
            </Card>
        </DefaultLayout>
    );
}

function GamesListSortable({
    apiData,
    setApiData,
    selectionId,
    setErrors,
    selectedGame,
    setSelectedGame,
    apiSelectionGameData,
}) {
    const { t } = useTranslation();
    const { slug } = useParams();
    const [allSelected, setAllSelected] = useState(false);
    const [sortParams, setSortParams] = useState("");
    const { api_get, api_post } = useRequest();
    const [sortingId, setSortingId] = useState(null);
    const sensors = useSensors(useSensor(PointerSensor));

    const handleUploadGames = () => {
        const getParams = buildGetParamsFromApiData({
            apiData: apiSelectionGameData,
            prefix: "filter",
            filterEmptyStrings: true,
        });
        const url =
            url_reverse("api:event:selection:item:list", { event_slug: slug, selection_id: selectionId }) +
            "?" +
            buildQS({ page: apiData.pager?.number + 1, ...getParams });
        api_get(null, url, {
            then: (resp) => {
                if (resp.status === "ok") {
                    setApiData((prevState) => {
                        return {
                            ...prevState,
                            object_list: apiData.object_list.concat(resp.object_list),
                            forms_data: resp.forms_data,
                            pager: resp.pager,
                            selection: resp.selection,
                            status: resp.status,
                        };
                    });
                } else {
                    setErrors(resp.errors);
                }
            },
        });
    };
    const handleSort = (params) => {
        const url =
            url_reverse("api:event:selection:item:list", { event_slug: slug, selection_id: selectionId }) +
            "?" +
            `&sort=${params}`;
        api_get(null, url, {
            then: (resp) => {
                if (resp.status === "ok") {
                    setApiData((prevState) => {
                        return {
                            ...prevState,
                            object_list: resp.object_list,
                            forms_data: resp.forms_data,
                            pager: resp.pager,
                            selection: resp.selection,
                            status: resp.status,
                        };
                    });
                    setSortParams(params);
                } else {
                    setErrors(resp.errors);
                }
            },
        });
    };
    const handleCheckBoxChange = (id) => {
        if (selectedGame.includes(id)) {
            setSelectedGame(selectedGame.filter((el) => el !== id));
        } else {
            setSelectedGame([...selectedGame, id]);
        }
    };

    const handleSelectAll = () => {
        if (allSelected) {
            setAllSelected(false);
            setSelectedGame([]);
        } else {
            setAllSelected(true);
            setSelectedGame([]);
            setSelectedGame(apiData.object_list.map((el) => el.game.id));
        }
    };
    const prepareTooltip = (data) => {
        if (data) {
            return data.map((el) => {
                return <div key={el.id}> {`${el.__label}-${el.date.slice(0, -3)}`}</div>;
            });
        }
    };
    const prepareDataTooltip = (data) => {
        if (data) {
            return (
                <div>
                    {t("Дата и время принятия участия: ")} {data.slice(0, -3)}
                </div>
            );
        }
        return (
            <div>
                {t("Дата и время принятия участия: ")} {"-"}
            </div>
        );
    };
    const SrtItem = ({ id, el }) => {
        const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
            id,
        });

        const style = {
            transform: CSS.Translate.toString(transform),
            transition,
        };

        const backGround = el.is_excluded ? "#6c757d59" : "";

        return (
            <tr ref={setNodeRef} style={{ ...style, backgroundColor: backGround }}>
                <td {...listeners} {...attributes}>
                    {id === sortingId ? (
                        <Spinner animation="border" variant="primary" size="sm" />
                    ) : (
                        <Awesome type="bars" />
                    )}
                </td>
                <td>
                    <Link to={url_reverse("games:admin", { game_id: el.game.id })}>
                        {el.game.base_locale_object.name}
                    </Link>
                </td>
                <td>
                    {el.is_send ? t("Отправлена") : t("Не отправлена")}{" "}
                    <Awesome type="envelope" tooltip={prepareTooltip(el.__dev_newsletters)} />{" "}
                </td>
                <td>
                    {el.moderation_state === "material_request" ? t("Не принял") : t("Принял")}{" "}
                    <Awesome type="info-circle" tooltip={prepareDataTooltip(el.date_accepted_to_participate)} />{" "}
                </td>
                <td>{moderationState[el.moderation_state]}</td>
                <td>{publicationState[el.game.publication_state]}</td>
                <td>
                    {el.game.publication_state === "done" && el.moderation_state === "review_passed" ? (
                        <Badge variant={"success"}>{t("Да")}</Badge>
                    ) : (
                        <Badge variant={"danger"}>{t("Нет")}</Badge>
                    )}
                </td>
                <td>
                    <Link
                        to={url_reverse("admin:event:selection:items:edit", {
                            event_slug: slug,
                            selection_slug: apiData.selection.slug,
                            item_id: el.id,
                        })}
                        className="btn btn-sm btn-outline-primary ml-1"
                    >
                        <Awesome type="pencil" />
                    </Link>
                </td>
                <td>
                    <DevForm.Checkbox
                        checked={selectedGame.includes(el.game_id)}
                        onChange={() => handleCheckBoxChange(el.game_id)}
                    />
                </td>
            </tr>
        );
    };

    const onSortEnd = (event) => {
        const { active, over } = event;

        if (active.id !== over.id) {
            const newIndex = apiData.object_list.findIndex((item) => item.id === over.id);
            const oldIndex = apiData.object_list.findIndex((item) => item.id === active.id);
            const new_list = arrayMove(apiData.object_list, oldIndex, newIndex);
            const ids = new_list.map((o) => o.id);

            const newApiData = { ...apiData };
            newApiData.object_list = new_list;
            setApiData(newApiData);

            setSortingId(active.id);

            api_post(
                null,
                url_reverse("api:event:selection:item:list", { event_slug: slug, selection_id: selectionId }),
                {
                    params: { action: "rearrange", ids },
                    finally: () => setSortingId(null),
                }
            );
        }
    };

    const itemIds = useMemo(() => (apiData?.object_list ? apiData.object_list.map((item) => item.id) : []), [apiData]);

    return (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onSortEnd}>
            <div style={{ overflowX: "auto", maxWidth: "100%" }}>
                <Table responsive className="dmg-table">
                    <thead>
                        <tr>
                            <th style={{ width: "30px" }} />
                            <th>{t("Игра")}</th>
                            <th onClick={() => handleSort(sortParams === "is_send" ? "-is_send" : "is_send")}>
                                {t("Рассылка")}
                            </th>
                            <th onClick={() => handleSort(sortParams === "ready" ? "-ready" : "ready")}>
                                {t("Участие")}
                            </th>
                            <th>{t("Материалы")}</th>
                            <th>{t("Модерация")}</th>
                            <th onClick={() => handleSort(sortParams === "accepted" ? "-accepted" : "accepted")}>
                                {t("Готов к размещению")}
                            </th>
                            <th></th>
                            <th>
                                <DevForm.Checkbox checked={allSelected} onChange={handleSelectAll} />
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        <SortableContext items={itemIds} strategy={verticalListSortingStrategy}>
                            {apiData?.object_list &&
                                apiData.object_list.map((el) => <SrtItem id={el.id} key={el.id} el={el} />)}
                        </SortableContext>
                    </tbody>
                </Table>
                {apiData.pager?.number < apiData.pager?.num_pages && (
                    <a onClick={handleUploadGames}>{t("Загрузить еще")}</a>
                )}
            </div>
        </DndContext>
    );
}
